//src/components/TickerInput/TickerInput.js

import { useState, useEffect, useRef, useMemo } from "react";
import { getAllTickers, validateTickerWithAPI, isValidTickerLocal } from "../../utils/tickerValidator";
import "./TickerInput.css";
import loadingStyles from './../../styles/Common.module.css';
import { useTranslation } from "react-i18next";

function TickerInput({ stocks, onStocksChange }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [allTickers, setAllTickers] = useState([]); // 자동완성용 데이터 상태
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const suggestionListRef = useRef(null);
  const componentRef = useRef(null);

  // 2. 컴포넌트 마운트 시 유틸리티를 통해 자동완성용 데이터 포맷팅
  useEffect(() => {
    const rawData = getAllTickers();
    const formattedData = rawData.map(item => ({
      symbol: item.s.toUpperCase(),
      name: item.n ? item.n.toUpperCase() : "NO NAME",
      type: item.t,
      exchange: item.e
    }));
    setAllTickers(formattedData);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        setSuggestions([]);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 3. 입력 핸들러: allTickers 상태를 사용하여 검색 수행
  const handleInputChange = (e) => {
    const input = e.target.value.toUpperCase().trim();
    setQuery(input);
    setErrorMessage("");
    setSelectedIndex(-1);
  
    if (input.length > 0) {
      const startsWithMatch = allTickers.filter(item => item.symbol.startsWith(input));
      const containsMatch = allTickers.filter(
        item => item.symbol.includes(input) && !item.symbol.startsWith(input)
      );
      
      const filtered = [...startsWithMatch, ...containsMatch].slice(0, 50);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  // 4. 종목 추가 로직
  const addStock = async (ticker) => {
    if (!ticker) return;
    ticker = ticker.trim().toUpperCase();

    // 1. 로컬 데이터셋 확인
    if (isValidTickerLocal(ticker)) {
      onStocksChange((prevStocks) => {
        if (!prevStocks.includes(ticker)) {
          return [...prevStocks, ticker];
        }
        return prevStocks;
      });
      setQuery("");
      setErrorMessage("");
      
      // ✅ 이 부분(리스트 닫기)이 있는지 꼭 확인하세요!
      setSuggestions([]); 
      setSelectedIndex(-1);
      return; 
    }

    // 2. 로컬에 없다면 서버 API 호출
    try {
      setIsLoading(true);
      const verifiedTicker = await validateTickerWithAPI(ticker);
      
      if (verifiedTicker) {
        // 서버에서 검증된 티커(예: AA.US)를 가져옴
        onStocksChange((prevStocks) => {
          if (!prevStocks.includes(verifiedTicker)) {
            return [...prevStocks, verifiedTicker];
          }
          return prevStocks;
        });
        setQuery("");
        setErrorMessage("");
      } else {
        // 서버에서도 찾지 못한 경우
        setErrorMessage(`${t("notValidTicker")}`);
      }
    } finally {
      setIsLoading(false);
      setSuggestions([]);
      setSelectedIndex(-1);
    }
  };

  const removeStock = (ticker) => {
    onStocksChange((prevStocks) => prevStocks.filter((s) => s !== ticker));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        addStock(suggestions[selectedIndex].symbol);
      } else if (query.length > 0) {
        addStock(query);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    }
  };

  // 자동완성 스크롤 로직 유지
  useEffect(() => {
    if (selectedIndex < 0 || !suggestionListRef.current) return;
    const listElement = suggestionListRef.current;
    const selectedItem = listElement.children[selectedIndex];
    if (!selectedItem) return;
    
    const itemTop = selectedItem.offsetTop;
    const itemBottom = itemTop + selectedItem.offsetHeight;
    const listTop = listElement.scrollTop;
    const listBottom = listTop + listElement.clientHeight;

    if (itemTop < listTop) listElement.scrollTop = itemTop;
    else if (itemBottom > listBottom) listElement.scrollTop = itemBottom - listElement.clientHeight;
  }, [selectedIndex, suggestions]);

  return (
    <div className="portfolio-input" ref={componentRef}> 
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={""}
        // placeholder={t("enterTickerPlaceholder") || "Ticker 입력..."}
        disabled={isLoading}
      />
      {isLoading && (
        <div className={loadingStyles.smallRowLoadingContainer}>
          <div className={loadingStyles.spinner}></div>
           <p>{t("checkingTicker")}</p>
        </div>
      )}
      {suggestions.length > 0 && (
        <ul className="suggestions" ref={suggestionListRef}>
          {suggestions.map((item, index) => (
            <li
              key={`${item.symbol}-${index}`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => addStock(item.symbol)}
              className={selectedIndex === index ? "selected" : ""}
            >
              <strong>{item.symbol}</strong> - {item.name} <small>({item.type} | {item.exchange})</small>
            </li>
          ))}
        </ul>
      )}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      <div className="selected-stocks">
        {stocks.map((ticker) => (
          <span key={ticker} className="stock-tag">
            {ticker} <button onClick={() => removeStock(ticker)}>✖</button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default TickerInput;